import { useEffect, useMemo, useRef } from 'react';
import type { ChatMessage } from '@/shared/types';
import { formatMessageTimestamp } from '@/shared/text';
import { Markdown } from './Markdown';

export function ChatTranscript({
  messages,
  pending,
  streamingText,
  onChapterLink,
}: {
  messages: ChatMessage[];
  pending: boolean;
  streamingText?: string;
  onChapterLink?: (chapterId: string) => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Both read paths already return oldest→newest, but sort defensively so a
  // mis-ordered caller can't scramble the transcript. `created_at` ties are
  // broken by the assistant's deliberate +1ms stamp (see background/chat.ts).
  const ordered = useMemo(
    () => [...messages].sort((a, b) => a.created_at - b.created_at),
    [messages],
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [ordered, pending, streamingText]);

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto bg-card px-3 py-2 text-sm"
    >
      {messages.length === 0 && !pending && (
        <p className="p-2 text-xs text-slate-500">
          Ask a question about anything in your library. The assistant searches your chapters
          and loads the relevant ones to answer.
        </p>
      )}
      {ordered.map((m) => (
        <div
          key={m.id}
          className={`my-1 rounded px-2 py-1 ${
            m.role === 'user'
              ? // blue-* is static (not part of the inverting ink ramp), so in
                // dark mode darken the bubble bg to keep the inverted (light)
                // text readable.
                'ml-2 bg-blue-50 text-slate-900 dark:bg-blue-900 @md:ml-6'
              : 'mr-2 bg-slate-100 text-slate-900 @md:mr-6'
          }`}
        >
          <div className="mb-0.5 flex items-baseline justify-between gap-2 text-[10px] uppercase tracking-wide text-slate-500">
            <span>{m.role}</span>
            <time
              dateTime={new Date(m.created_at).toISOString()}
              className="normal-case tracking-normal tabular-nums"
            >
              {formatMessageTimestamp(m.created_at)}
            </time>
          </div>
          {m.role === 'assistant' ? (
            <Markdown source={m.content} onChapterLink={onChapterLink} />
          ) : (
            <div className="whitespace-pre-wrap text-sm">{m.content}</div>
          )}
        </div>
      ))}
      {pending && (
        <div className="mr-2 my-1 rounded bg-slate-100 px-2 py-1 text-sm text-slate-900 @md:mr-6">
          <div className="mb-0.5 text-[10px] uppercase tracking-wide text-slate-500">
            assistant
          </div>
          {streamingText ? (
            <Markdown source={streamingText} trailingCaret onChapterLink={onChapterLink} />
          ) : (
            <span className="inline-block animate-pulse text-slate-500">thinking…</span>
          )}
        </div>
      )}
    </div>
  );
}
